import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { fetchUserMembershipStatuses } from '@/modules/core/data/memberRepository';
import { SessionExpiredDialog } from '@/components/SessionExpiredDialog';
import { fetchProfile as fetchProfileFromRepo, ensureProfileExists } from '@/modules/core/data/profileRepository';
import type { Profile } from '@/types/database';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const loadProfile = async (userId: string, userEmail?: string): Promise<Profile | null> => {
    try {
      if (userEmail) {
        // Ensure profile exists (creates if missing)
        const profileData = await ensureProfileExists(userId, userEmail);
        setProfile(profileData);
        return profileData;
      } else {
        const profileData = await fetchProfileFromRepo(userId);
        setProfile(profileData);
        return profileData;
      }
    } catch (err) {
      console.error('Error in loadProfile:', err);
      return null;
    }
  };

  // Clear stale session if user no longer exists
  const clearStaleSession = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Ignore errors during cleanup
    }
    setSession(null);
    setUser(null);
    setProfile(null);
  };

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (!isMounted) return;

        // Skip profile loading on password recovery route — the recovery session
        // is short-lived and the recovery JWT is not accepted by ensure-profile.
        const isRecoveryRoute = typeof window !== 'undefined'
          && window.location.pathname.startsWith('/reset-password');

        // If there's a session error or the session token is invalid, clear it
        if (sessionError) {
          console.error('Session error during init:', sessionError);
          await clearStaleSession();
          setLoading(false);
          return;
        }

        // Validate the session by trying to get the user
        if (currentSession?.access_token) {
          const { data: { user: validatedUser }, error: userError } = await supabase.auth.getUser(currentSession.access_token);
          
          if (!isMounted) return;

          if (userError || !validatedUser) {
            // User doesn't exist anymore - clear the stale session
            console.warn('Stale session detected, clearing...', userError?.message);
            await clearStaleSession();
            setLoading(false);
            return;
          }

          // Valid user, set state
          setSession(currentSession);
          setUser(validatedUser);
          
          // Try to fetch profile, but don't block on failure.
          // Skip during password recovery (profile already exists, recovery token is rejected by ensure-profile).
          if (!isRecoveryRoute) {
            await loadProfile(validatedUser.id, validatedUser.email);
          }
        } else {
          // No session
          setSession(null);
          setUser(null);
        }
        
        if (isMounted) setLoading(false);
      } catch (err) {
        console.error('Error during auth initialization:', err);
        if (isMounted) {
          await clearStaleSession();
          setLoading(false);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!isMounted) return;

        const userChanged = newSession?.user?.id !== user?.id;
        const signedOut = event === 'SIGNED_OUT';
        const signedIn = event === 'SIGNED_IN';
        const tokenRefreshed = event === 'TOKEN_REFRESHED';
        
        // Detect session expiration
        if (event === 'TOKEN_REFRESHED' && !newSession) {
          // Token refresh failed - session expired
          setSessionExpired(true);
          return;
        }
        
        if (signedOut || signedIn || userChanged) {
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setSessionExpired(false); // Reset on any auth change

          if (newSession?.user && userChanged) {
            const isRecoveryRoute = typeof window !== 'undefined'
              && window.location.pathname.startsWith('/reset-password');
            setTimeout(() => {
              if (isMounted && !isRecoveryRoute) {
                loadProfile(newSession.user.id, newSession.user.email);
              }
            }, 0);
            
            // Note: Welcome email and consent are now handled after code verification in Auth.tsx
            // We no longer use the email confirmation link flow
          }
          
          if (signedOut) {
            setProfile(null);
          }
        } else if (tokenRefreshed && newSession) {
          setSession(newSession);
          setSessionExpired(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Check for session expiration periodically
  useEffect(() => {
    if (!session) return;

    const checkSession = async () => {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      
      if (error || !currentSession) {
        // Session is no longer valid
        if (session) {
          setSessionExpired(true);
        }
      }
    };

    // Check session every 5 minutes
    const interval = setInterval(checkSession, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [session]);

  const signIn = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      return { error: error as Error };
    }

    // Check if user is suspended in all their organizations
    if (data.user) {
      const memberships = await fetchUserMembershipStatuses(data.user.id);
      if (memberships.length > 0) {
        const allSuspended = memberships.every(m => m.status === 'suspended');
        const allSuspendedOrRemoved = memberships.every(m => m.status === 'suspended' || m.status === 'removed');

        if (allSuspendedOrRemoved) {
          // Sign out immediately
          await supabase.auth.signOut();
          const msg = allSuspended
            ? 'Votre compte a été suspendu. Veuillez contacter un administrateur pour résoudre ce problème.'
            : 'Votre accès a été révoqué. Veuillez contacter un administrateur.';
          return { error: new Error(msg) };
        }
      }
    }

    setSessionExpired(false);
    return { error: null };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    // Use Supabase native auth with auto_confirm_email enabled
    // No emailRedirectTo - we use our custom 4-digit code verification flow
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    // With auto_confirm_email enabled, no native Supabase email is sent
    // Our custom verification code will be sent via Edge Function in EmailVerification component

    return { error: error as Error | null, data };
  };

  const signOut = async () => {
    try {
      sessionStorage.removeItem('lapyno_space_preference');
    } catch {
      // Session storage not available
    }
    await supabase.auth.signOut();
    setProfile(null);
    setSessionExpired(false);
  };

  const refreshProfile = async () => {
    if (user) {
      await loadProfile(user.id, user.email);
    }
  };

  const handleSessionRefresh = () => {
    window.location.reload();
  };

  const handleSessionLogout = async () => {
    setSessionExpired(false);
    await signOut();
    window.location.href = '/';
  };

  // Batch 5A (Q3): memoize context value so consumers do not re-render on every
  // AuthProvider render (e.g. when unrelated state like `sessionExpired` changes).
  const value = useMemo<AuthContextType>(
    () => ({
      user,
      session,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      refreshProfile,
    }),
    [user, session, profile, loading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredDialog
        open={sessionExpired}
        onRefresh={handleSessionRefresh}
        onLogout={handleSessionLogout}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
